/**
 * The price table — the single source of truth for what a plan costs.
 *
 * Prices live here and nowhere else. The marketing page
 * (apps/web/public/landing-d6/pricing.html) is hand-authored static HTML; a
 * test pins its data-m attributes to this table so the two cannot drift.
 * Before this table existed, three surfaces disagreed: /pricing said Pro $97,
 * the in-app plan page said $39, and the enum had no `lite` at all.
 *
 * No FX conversion is ever computed. INR is a founder-set table, not a
 * conversion of USD — a runtime rate lookup would make the displayed price
 * drift from the charged one.
 */

export const SELLABLE_PLANS = ['lite', 'pro', 'max', 'ultra'] as const;
export type SellablePlan = typeof SELLABLE_PLANS[number];

export type BillingCurrency = 'USD' | 'INR';
export type BillingPeriod = 'monthly' | 'annual';

export interface PlanPrice {
  /** The per-month figure shown to the user, in MAJOR units (dollars, rupees). */
  displayMonthly: number;
  /** What we actually charge, in MINOR units (cents, paise). */
  chargeMinor: number;
}

/** The paid door's welcome — granted once at first paid activation, mirrored by the server grant. */
export const SUBSCRIBE_WELCOME_CREDITS = 1_000;

/** Monthly credit allowance. INR is deliberately priced at one rupee per credit. */
export const PLAN_CREDITS: Record<SellablePlan, number> = {
  lite: 2_000,
  pro: 10_000,
  max: 20_000,
  ultra: 50_000,
};

/**
 * Annual rows carry the per-month rate the UI displays under "billed
 * annually"; chargeMinor is the once-yearly total (rate * 12 * 100).
 * The annual rate is floor(monthly * 0.9) in both currencies.
 */
export const PRICES: Record<BillingCurrency, Record<SellablePlan, Record<BillingPeriod, PlanPrice>>> = {
  USD: {
    lite:  { monthly: { displayMonthly: 19,  chargeMinor: 1_900 },  annual: { displayMonthly: 17,  chargeMinor: 20_400 } },
    pro:   { monthly: { displayMonthly: 97,  chargeMinor: 9_700 },  annual: { displayMonthly: 87,  chargeMinor: 104_400 } },
    max:   { monthly: { displayMonthly: 197, chargeMinor: 19_700 }, annual: { displayMonthly: 177, chargeMinor: 212_400 } },
    ultra: { monthly: { displayMonthly: 497, chargeMinor: 49_700 }, annual: { displayMonthly: 447, chargeMinor: 536_400 } },
  },
  INR: {
    lite:  { monthly: { displayMonthly: 2_000,  chargeMinor: 200_000 },   annual: { displayMonthly: 1_800,  chargeMinor: 2_160_000 } },
    pro:   { monthly: { displayMonthly: 10_000, chargeMinor: 1_000_000 }, annual: { displayMonthly: 9_000,  chargeMinor: 10_800_000 } },
    max:   { monthly: { displayMonthly: 20_000, chargeMinor: 2_000_000 }, annual: { displayMonthly: 18_000, chargeMinor: 21_600_000 } },
    ultra: { monthly: { displayMonthly: 50_000, chargeMinor: 5_000_000 }, annual: { displayMonthly: 45_000, chargeMinor: 54_000_000 } },
  },
};

export function getPlanPrice(
  plan: SellablePlan,
  currency: BillingCurrency,
  period: BillingPeriod,
): PlanPrice {
  return PRICES[currency][plan][period];
}

export function isSellablePlan(value: string): value is SellablePlan {
  return (SELLABLE_PLANS as readonly string[]).includes(value);
}

/**
 * Which currency a country is billed in.
 *
 * India settles in INR because recurring INR cards must clear Razorpay under
 * the RBI e-mandate; everywhere else settles in USD through xPay. The server
 * derives the GATEWAY from this (see gateway-router.ts) rather than testing the
 * country a second time, and the plan page derives the DISPLAYED currency from
 * it — so the price a user reads and the rail that charges them can never
 * disagree about who is Indian.
 *
 * Accepts an ISO-3166-1 alpha-2 code, case-insensitively.
 */
export function currencyForCountry(country: string): BillingCurrency {
  return country.trim().toUpperCase() === 'IN' ? 'INR' : 'USD';
}
